/**
 * Diagnostics.js — Executive Dashboard
 * Row 1: Executive Summary (Total Assets, Missing Critical, Compliance %, High Risk, Emergency Status)
 * Row 2: Patch Health (Success Rate, Rollback Count, Pending, Failed, Active Agents)
 * Row 3: Risk & Vulnerability (Unpatched CVEs, Top 10 High Risk Devices, Risk Trend)
 * Row 4: Recent Activity (Latest Releases, Recent Deployments)
 * Tab 2: Server Diagnostics (original)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { api } from '../api';

function getHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function timeAgo(d) {
  if (!d) return '—';
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const day = Math.floor(diff / 86400000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${day}d ago`;
}

// ── Mini sparkline bar ─────────────────────────────────────────────────────
function MiniBar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6, width: '100%', overflow: 'hidden' }}>
      <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s' }} />
    </div>
  );
}

// ── Gauge ring ─────────────────────────────────────────────────────────────
function GaugeRing({ value, label, color, size = 80 }) {
  const pct = Math.min(100, Math.max(0, value || 0));
  const r = (size - 12) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
      <svg width={size} height={size}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#e8ecf4" strokeWidth={8} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform={`rotate(-90 ${size/2} ${size/2})`} />
        <text x={size/2} y={size/2 + 5} textAnchor="middle" fontSize={14} fontWeight={700} fill={color}>
          {pct}%
        </text>
      </svg>
      <span style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>{label}</span>
    </div>
  );
}

// ── Executive KPI card ─────────────────────────────────────────────────────
function KpiCard({ icon, label, value, sub, color, bg, trend, alert, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: bg || '#fff', borderRadius: 12, padding: '18px 20px',
      border: `1px solid ${alert ? '#fca5a5' : '#e8ecf4'}`,
      boxShadow: alert ? '0 2px 12px rgba(220,38,38,0.1)' : '0 1px 4px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', gap: 6, position: 'relative', overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.1s, box-shadow 0.1s',
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,0.1)'; }}}
    onMouseLeave={e => { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=alert?'0 2px 12px rgba(220,38,38,0.1)':'0 1px 4px rgba(0,0,0,0.04)'; }}
    >
      {alert && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: '#dc2626' }} />}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 22 }}>{icon}</span>
        {trend !== undefined && (
          <span style={{ fontSize: 11, fontWeight: 600, color: trend >= 0 ? '#16a34a' : '#dc2626' }}>
            {trend >= 0 ? '▲' : '▼'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, color: color || '#1e2a3b', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8' }}>{sub}</div>}
    </div>
  );
}

// ── Status badge ───────────────────────────────────────────────────────────
function StatusBadge({ value }) {
  const ok   = ['ok','running','present','exists','healthy','set'].some(v => String(value).toLowerCase().includes(v));
  const warn = ['unknown','checking'].some(v => String(value).toLowerCase().includes(v));
  const color = ok ? '#16a34a' : warn ? '#d97706' : '#dc2626';
  const bg    = ok ? '#dcfce7' : warn ? '#fef3c7' : '#fee2e2';
  return (
    <span style={{ background: bg, color, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>
      {String(value)}
    </span>
  );
}

function DiagRow({ label, value }) {
  return (
    <tr>
      <td style={{ padding: '8px 16px', fontSize: 13, color: '#94a3b8', width: 220 }}>{label}</td>
      <td style={{ padding: '8px 16px', fontSize: 13 }}>
        {typeof value === 'object'
          ? <pre style={{ margin: 0, fontSize: 11, color: '#94a3b8' }}>{JSON.stringify(value, null, 2)}</pre>
          : <StatusBadge value={value} />}
      </td>
    </tr>
  );
}

function DiagSection({ title, icon, data }) {
  return (
    <div className="table-wrap section-gap">
      <div className="table-header"><div className="table-title">{icon} {title}</div></div>
      <table style={{ width: '100%' }}>
        <tbody>
          {Object.entries(data || {}).map(([k, v]) => (
            <DiagRow key={k} label={k} value={typeof v === 'object' && v !== null ? v : String(v)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

const SEV_COLOR = { critical: '#dc2626', high: '#ea580c', medium: '#d97706', low: '#16a34a' };
const SEV_BG    = { critical: '#fee2e2', high: '#ffedd5', medium: '#fef3c7', low: '#dcfce7' };

export default function Diagnostics() {
  const [tab, setTab] = useState('executive');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [execDrillDown, setExecDrillDown] = useState(null); // 'critical'|'compliance'|'risk'|'emergency'|null

  // Executive data
  const [execData, setExecData] = useState(null);

  // Server diagnostics data
  const [diagData,   setDiagData]   = useState(null);
  const [endpoints,  setEndpoints]  = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError,   setDiagError]   = useState('');
  const [publicIp,  setPublicIp]  = useState('');
  const [saving,    setSaving]    = useState(false);
  const [saveMsg,   setSaveMsg]   = useState('');

  // ── Load executive data from all modules ─────────────────────────────────
  const loadExecutive = useCallback(async () => {
    setLoading(true);
    try {
      const [
        connRes, compRes, vulnRes, relRes, pendRes, agentRes
      ] = await Promise.allSettled([
        api.getConnectionStatus(),
        api.getCompliance(),
        axios.get('/api/compliance/risk-matrix', { headers: getHeaders() }),
        api.getPatchReleases(),
        api.getPendingDevices(),
        axios.get('/api/diagnostics', { headers: getHeaders() })
      ]);

      const conn     = connRes.status === 'fulfilled'  ? connRes.value.data : {};
      const comp     = compRes.status === 'fulfilled'  ? compRes.value.data : {};
      const vuln     = vulnRes.status === 'fulfilled'  ? vulnRes.value.data : {};
      const rel      = relRes.status === 'fulfilled'   ? relRes.value.data  : {};
      const pend     = pendRes.status === 'fulfilled'  ? pendRes.value.data : {};
      const diag     = agentRes.status === 'fulfilled' ? agentRes.value.data : {};

      // ── Compute all metrics ───────────────────────────────────────────────
      const devices      = conn.devices || [];
      const totalAssets  = devices.length;
      const onlineAssets = devices.filter(d => d.connectionHealth === 'healthy').length;
      const offlineAssets = totalAssets - onlineAssets;

      const details = comp.details || [];
      const totalStatuses   = details.length;
      const successStatuses = details.filter(s => s.status === 'success').length;
      const failedStatuses  = details.filter(s => s.status === 'failed').length;
      const pendingStatuses = details.filter(s => s.status === 'pending').length;
      const inProgressStatuses = details.filter(s => s.status === 'in_progress').length;
      const rollbackStatuses = details.filter(s => s.isRollback).length;
      const emergencyStatuses = details.filter(s => s.isEmergency);
      const emergencySuccess = emergencyStatuses.filter(s => s.status === 'success').length;
      const emergencyFailed  = emergencyStatuses.filter(s => s.status === 'failed').length;

      // Compliance % = success / (success + failed) * 100
      const compPct = (successStatuses + failedStatuses) > 0
        ? Math.round((successStatuses / (successStatuses + failedStatuses)) * 100)
        : 0;

      // Patch success rate = success / total deployed (success + failed)
      const patchSuccessRate = (successStatuses + failedStatuses) > 0
        ? Math.round((successStatuses / (successStatuses + failedStatuses)) * 100)
        : 0;

      // Assets missing critical patches
      const criticalPendingByDevice = {};
      details.forEach(s => {
        if (s.patch?.severity === 'critical' && s.status === 'pending') {
          const id = s.device?.id || s.deviceId;
          if (id) criticalPendingByDevice[id] = (criticalPendingByDevice[id] || 0) + 1;
        }
      });
      const assetsMissingCritical = Object.keys(criticalPendingByDevice).length;

      // Vulnerabilities
      const vulnList = vuln.vulnerabilities || vuln.data || [];
      const criticalVulns = vulnList.filter(v => v.severity === 'critical' || (v.cvss_score || v.cvssScore || 0) >= 9).length;
      const highVulns     = vulnList.filter(v => v.severity === 'high' || ((v.cvss_score || v.cvssScore || 0) >= 7 && (v.cvss_score || v.cvssScore || 0) < 9)).length;
      const unpatchedVulns = vulnList.filter(v => v.status !== 'patched').length;

      // High risk devices — from risk matrix or derive from patch statuses
      const highRiskDevices = (vuln.topRiskDevices || vuln.highRiskDevices || []).slice(0, 10);

      // Derive high risk count from devices with multiple critical pending
      const highRiskCount = Object.values(criticalPendingByDevice).filter(c => c >= 2).length
        || highRiskDevices.length;

      // Recent releases
      const releases    = rel.releases || [];
      const recentCritical = releases.filter(r => r.severity === 'critical').slice(0, 5);
      const zeroDayAlerts  = rel.zeroDayAlerts || [];

      // Pending devices
      const pendingDevices = pend.devices || [];
      const pendingSummary = pend.summary || {};

      // DB table counts from diagnostics
      const dbTables = diag.database?.tables || {};

      setExecData({
        // Summary metrics
        totalAssets, onlineAssets, offlineAssets,
        assetsMissingCritical,
        compPct,
        highRiskCount,
        zeroDayAlerts,

        // Patch health
        totalStatuses, successStatuses, failedStatuses,
        pendingStatuses, inProgressStatuses,
        rollbackStatuses,
        patchSuccessRate,
        emergencyStatuses: emergencyStatuses.length,
        emergencySuccess, emergencyFailed,

        // Vulnerability
        criticalVulns, highVulns, unpatchedVulns,
        vulnList: vulnList.slice(0, 10),
        highRiskDevices,

        // Recent
        recentCritical,
        releases: releases.slice(0, 8),
        pendingDevices: pendingDevices.slice(0, 5),
        pendingSummary,

        // DB
        dbTables,
        serverUptime: diag.server?.uptime || '—',
      });

      setLastRefresh(new Date());
    } catch (e) {
      console.error('Executive dashboard load error:', e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load server diagnostics ───────────────────────────────────────────────
  const loadDiag = useCallback(async () => {
    setDiagLoading(true);
    setDiagError('');
    try {
      const [diagRes, epRes] = await Promise.all([
        axios.get('/api/diagnostics', { headers: getHeaders(), timeout: 15000 }),
        axios.get('/api/diagnostics/endpoints', { headers: getHeaders(), timeout: 8000 }).catch(() => ({ data: null }))
      ]);
      setDiagData(diagRes.data);
      if (epRes.data) {
        setEndpoints(epRes.data);
        const pub = epRes.data.endpoints.find(e => e.type === 'public');
        if (pub) setPublicIp(pub.ip);
      }
    } catch (e) { setDiagError(`Failed: ${e.message}`); }
    finally { setDiagLoading(false); }
  }, []);

  useEffect(() => {
    loadExecutive();
  }, [loadExecutive]);

  useEffect(() => {
    if (tab === 'diagnostics') loadDiag();
  }, [tab, loadDiag]);

  async function savePublicIp() {
    setSaving(true); setSaveMsg('');
    try {
      await axios.post('/api/diagnostics/endpoints/public-ip',
        { publicIp, publicPort: 3031 }, { headers: getHeaders() });
      setSaveMsg('Saved.');
      await loadDiag();
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); setTimeout(() => setSaveMsg(''), 5000); }
  }

  const d = execData;

  return (
    <div>
      {/* ── Page header ── */}
      <div className="page-header">
        <div>
          <div className="page-title">
            {tab === 'executive' ? '📊 Executive Dashboard' : '🔧 Server Diagnostics'}
          </div>
          <div className="page-subtitle">
            {tab === 'executive'
              ? `Real-time security & patch posture · ${lastRefresh ? 'Updated ' + timeAgo(lastRefresh) : 'Loading...'}`
              : 'Backend health checks and server configuration'}
          </div>
        </div>
        <div className="flex-gap">
          {tab === 'executive' && (
            <button className="btn btn-ghost" onClick={loadExecutive} disabled={loading}>
              {loading ? '⟳ Loading...' : '↻ Refresh'}
            </button>
          )}
          {tab === 'diagnostics' && (
            <button className="btn btn-ghost" onClick={loadDiag} disabled={diagLoading}>
              {diagLoading ? '⟳ Running...' : '↻ Run Checks'}
            </button>
          )}
        </div>
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', borderBottom: '2px solid #e8ecf4', marginBottom: 24 }}>
        {[
          { id: 'executive',   label: '📊 Executive Dashboard' },
          { id: 'diagnostics', label: '🔧 Server Diagnostics' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 20px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? '#1a56db' : '#94a3b8',
            borderBottom: tab === t.id ? '2px solid #1a56db' : '2px solid transparent',
            marginBottom: -2
          }}>{t.label}</button>
        ))}
      </div>

      {/* ════════════════════════════════════════════════════════════════════
          EXECUTIVE DASHBOARD TAB
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'executive' && (
        loading ? <div className="loading">Loading executive dashboard...</div> : !d ? null : (
        <>
          {/* ── Zero-Day Banner ── */}
          {d.zeroDayAlerts?.length > 0 && (
            <div style={{ background: '#fff1f2', border: '2px solid #dc2626', borderRadius: 10,
              padding: '12px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 24 }}>⚠️</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#dc2626' }}>
                  {d.zeroDayAlerts.length} Zero-Day Alert{d.zeroDayAlerts.length > 1 ? 's' : ''} — Immediate Action Required
                </div>
                <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 2 }}>
                  {d.zeroDayAlerts.map(z => z.patchName).join(', ')} — critical patches with no devices patched
                </div>
              </div>
              <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700,
                background: '#dc2626', color: '#fff', padding: '4px 12px', borderRadius: 20 }}>
                🚨 CRITICAL
              </span>
            </div>
          )}

          {/* ════ ROW 1: Executive Summary ════ */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 12 }}>
              ▸ Executive Summary
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 12, marginBottom: 20 }}>
              <KpiCard icon="🖥️" label="Total Assets" value={d.totalAssets}
                sub={`${d.onlineAssets} online · ${d.offlineAssets} offline`}
                color="#1a56db" onClick={() => window.location.hash='#devices'} />
              <KpiCard icon="🔴" label="Missing Critical Patches" value={d.assetsMissingCritical}
                sub={`${d.assetsMissingCritical > 0 ? 'Devices with ≥1 critical pending' : 'All clear'}`}
                color={d.assetsMissingCritical > 0 ? '#dc2626' : '#16a34a'}
                alert={d.assetsMissingCritical > 0} onClick={() => setExecDrillDown('critical')} />
              <KpiCard icon="✅" label="Compliance %" value={`${d.compPct}%`}
                sub={`${d.successStatuses} patched of ${d.successStatuses + d.failedStatuses}`}
                color={d.compPct >= 80 ? '#16a34a' : d.compPct >= 60 ? '#d97706' : '#dc2626'}
                onClick={() => setExecDrillDown('compliance')} />
              <KpiCard icon="⚠️" label="High Risk Devices" value={d.highRiskCount}
                sub={`${d.criticalVulns} critical CVEs · ${d.highVulns} high`}
                color={d.highRiskCount > 0 ? '#ea580c' : '#16a34a'}
                alert={d.highRiskCount > 3} onClick={() => setExecDrillDown('risk')} />
              <KpiCard icon="🚨" label="Emergency Patch Status"
                value={d.emergencyStatuses > 0 ? `${d.emergencySuccess}/${d.emergencyStatuses}` : 'None'}
                sub={d.emergencyStatuses > 0 ? `${d.emergencySuccess} success · ${d.emergencyFailed} failed` : 'No emergency deployments'}
                color={d.emergencyFailed > 0 ? '#dc2626' : '#16a34a'}
                onClick={() => setExecDrillDown('emergency')} />
            </div>
          </div>

          {/* ── Drill-down panel (appears when KPI card is clicked) ── */}
          {execDrillDown && (
            <div style={{ background:'#f8fafc', border:'1px solid #e8ecf4', borderRadius:10,
              padding:'16px 20px', marginBottom:20 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#1e2a3b' }}>
                  {execDrillDown==='critical'    && '🔴 Devices with Critical Patches Pending'}
                  {execDrillDown==='compliance'  && '✅ Compliance Breakdown'}
                  {execDrillDown==='risk'        && '⚠️ High Risk Devices'}
                  {execDrillDown==='emergency'   && '🚨 Emergency Deployment History'}
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setExecDrillDown(null)}>✕ Close</button>
              </div>
              {execDrillDown === 'critical' && (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {d.pendingDevices.filter(dev => dev.criticalCount > 0).length === 0
                    ? <div style={{ color:'#16a34a', fontSize:13 }}>✅ No devices with critical patches pending</div>
                    : d.pendingDevices.filter(dev => dev.criticalCount > 0).map((dev,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px',
                        background:'#fff', borderRadius:8, borderLeft:'3px solid #dc2626' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{dev.device?.hostname}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>{dev.device?.os} · {dev.device?.ipAddress}</div>
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, background:'#fee2e2', color:'#dc2626', padding:'2px 8px', borderRadius:10 }}>
                          {dev.criticalCount} critical pending
                        </span>
                      </div>
                    ))
                  }
                </div>
              )}
              {execDrillDown === 'compliance' && (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12 }}>
                  {[
                    { label:'Total Deployments', value:d.totalStatuses, color:'#1a56db' },
                    { label:'Successful',         value:d.successStatuses, color:'#16a34a' },
                    { label:'Failed',             value:d.failedStatuses, color:'#dc2626' },
                    { label:'Pending',            value:d.pendingStatuses, color:'#d97706' },
                  ].map(item => (
                    <div key={item.label} style={{ background:'#fff', borderRadius:8, padding:'12px 16px',
                      borderLeft:`3px solid ${item.color}`, textAlign:'center' }}>
                      <div style={{ fontSize:22, fontWeight:800, color:item.color }}>{item.value}</div>
                      <div style={{ fontSize:11, color:'#64748b' }}>{item.label}</div>
                    </div>
                  ))}
                </div>
              )}
              {execDrillDown === 'risk' && (
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {d.recentCritical.filter(r => r.devicesQueued > 0).length === 0
                    ? <div style={{ color:'#16a34a', fontSize:13 }}>✅ No high risk items detected</div>
                    : d.recentCritical.filter(r => r.devicesQueued > 0).slice(0,8).map((r,i) => (
                      <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'8px 12px',
                        background:'#fff', borderRadius:8, borderLeft:'3px solid #ea580c' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight:600 }}>{r.patchName}</div>
                          <div style={{ fontSize:11, color:'#94a3b8' }}>{r.vendor} · v{r.newVersion}</div>
                        </div>
                        <span style={{ fontSize:11, fontWeight:700, background:'#ffedd5', color:'#ea580c', padding:'2px 8px', borderRadius:10 }}>
                          {r.devicesQueued} devices pending
                        </span>
                      </div>
                    ))
                  }
                </div>
              )}
              {execDrillDown === 'emergency' && (
                <div style={{ fontSize:13, color:'#64748b' }}>
                  {d.emergencyStatuses === 0
                    ? '✅ No emergency deployments have been made.'
                    : <><strong>{d.emergencyStatuses}</strong> emergency deployments · <span style={{ color:'#16a34a' }}>{d.emergencySuccess} succeeded</span> · <span style={{ color:'#dc2626' }}>{d.emergencyFailed} failed</span></>
                  }
                </div>
              )}
            </div>
          )}

          {/* ════ ROW 2: Patch Health ════ */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 12 }}>
              ▸ Patch Health
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 20 }}>

              {/* Patch Success Rate Gauge */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 16 }}>
                  Patch Success Rate
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
                  <GaugeRing value={d.patchSuccessRate}
                    color={d.patchSuccessRate >= 80 ? '#16a34a' : d.patchSuccessRate >= 60 ? '#d97706' : '#dc2626'}
                    label="Success Rate" size={90} />
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { label: 'Success',     value: d.successStatuses,    color: '#16a34a' },
                      { label: 'Failed',      value: d.failedStatuses,     color: '#dc2626' },
                      { label: 'Pending',     value: d.pendingStatuses,    color: '#d97706' },
                      { label: 'In Progress', value: d.inProgressStatuses, color: '#1a56db' },
                    ].map(item => (
                      <div key={item.label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                          <span style={{ fontSize: 11, color: '#64748b' }}>{item.label}</span>
                          <span style={{ fontSize: 11, fontWeight: 700, color: item.color }}>{item.value}</span>
                        </div>
                        <MiniBar value={item.value} max={d.totalStatuses} color={item.color} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Compliance Breakdown */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 16 }}>
                  Compliance Overview
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                  <GaugeRing value={d.compPct}
                    color={d.compPct >= 80 ? '#16a34a' : d.compPct >= 60 ? '#d97706' : '#dc2626'}
                    label="Compliance" size={90} />
                  <div style={{ flex: 1 }}>
                    {[
                      { label: 'Total Assets',      value: d.totalAssets, color: '#1a56db' },
                      { label: 'Fully Compliant',   value: Math.max(0, d.totalAssets - d.assetsMissingCritical), color: '#16a34a' },
                      { label: 'Missing Critical',  value: d.assetsMissingCritical, color: '#dc2626' },
                      { label: 'Pending Patches',   value: d.pendingSummary?.devicesWithPending || 0, color: '#d97706' },
                    ].map(item => (
                      <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between',
                        padding: '5px 0', borderBottom: '1px solid #f0f4ff' }}>
                        <span style={{ fontSize: 12, color: '#64748b' }}>{item.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: item.color }}>{item.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Rollback & Emergency */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 16 }}>
                  Operations Summary
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { icon: '↩', label: 'Rollback Count', value: d.rollbackStatuses, color: '#ea580c',
                      sub: 'Total rollbacks executed' },
                    { icon: '🚨', label: 'Emergency Deploys', value: d.emergencyStatuses, color: '#dc2626',
                      sub: `${d.emergencySuccess} succeeded · ${d.emergencyFailed} failed` },
                    { icon: '⏳', label: 'Patches Pending', value: d.pendingStatuses, color: '#d97706',
                      sub: 'Across all devices' },
                    { icon: '🖥️', label: 'Active Agents', value: d.onlineAssets, color: '#16a34a',
                      sub: `of ${d.totalAssets} total endpoints` },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', alignItems: 'center', gap: 12,
                      padding: '8px 12px', background: '#f8fafc', borderRadius: 8 }}>
                      <span style={{ fontSize: 20 }}>{item.icon}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>{item.label}</span>
                          <span style={{ fontSize: 14, fontWeight: 800, color: item.color }}>{item.value}</span>
                        </div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{item.sub}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ════ ROW 3: Security Risk & Vulnerabilities ════ */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 12 }}>
              ▸ Security Risk &amp; Vulnerability Dashboard
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>

              {/* Unpatched Critical Vulnerabilities */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b' }}>
                    Unpatched Critical Vulnerabilities
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2',
                      color: '#dc2626', padding: '2px 8px', borderRadius: 12 }}>
                      {d.criticalVulns} CRITICAL
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#ffedd5',
                      color: '#ea580c', padding: '2px 8px', borderRadius: 12 }}>
                      {d.highVulns} HIGH
                    </span>
                  </div>
                </div>
                {d.vulnList.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#16a34a' }}>
                    <div style={{ fontSize: 32 }}>✅</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>No unpatched vulnerabilities</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
                    {d.vulnList.map((v, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', background: '#f8fafc', borderRadius: 8,
                        borderLeft: `3px solid ${SEV_COLOR[v.severity] || '#94a3b8'}` }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>
                            {v.cve || v.name || 'Unknown CVE'}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {v.product || v.vendor || '—'} {v.cvss_score ? `· CVSS ${v.cvss_score}` : ''}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700,
                          background: SEV_BG[v.severity] || '#f1f5f9',
                          color: SEV_COLOR[v.severity] || '#64748b',
                          padding: '2px 7px', borderRadius: 10 }}>
                          {v.severity || 'unknown'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: 12, padding: '8px 12px', background: '#f8fafc', borderRadius: 8,
                  display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: '#64748b' }}>Total Unpatched</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: d.unpatchedVulns > 0 ? '#dc2626' : '#16a34a' }}>
                    {d.unpatchedVulns}
                  </span>
                </div>
              </div>

              {/* Top 10 High Risk Devices */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 14 }}>
                  Top High Risk Devices
                </div>
                {d.assetsMissingCritical === 0 && d.highRiskDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#16a34a' }}>
                    <div style={{ fontSize: 32 }}>🛡️</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>No high risk devices</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 250, overflowY: 'auto' }}>
                    {/* Devices with missing critical patches */}
                    {Object.entries(
                      (() => {
                        const m = {};
                        (d.recentCritical || []).forEach(r => {
                          if (r.devicesQueued > 0) m[r.patchName] = r.devicesQueued;
                        });
                        return m;
                      })()
                    ).slice(0, 5).map(([name, count], i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', background: '#fff8f0', borderRadius: 8,
                        borderLeft: '3px solid #ea580c' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#ffedd5',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: '#ea580c' }}>{i+1}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>{name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{count} devices pending</div>
                        </div>
                        <MiniBar value={count} max={Math.max(...Object.values((() => {const m={};(d.recentCritical||[]).forEach(r=>{if(r.devicesQueued>0)m[r.patchName]=r.devicesQueued;});return m;})()).filter(v=>v>0), 1)} color="#ea580c" />
                      </div>
                    ))}
                    {d.highRiskDevices.slice(0, 5).map((dev, i) => (
                      <div key={`hr-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', background: '#fff1f2', borderRadius: 8,
                        borderLeft: '3px solid #dc2626' }}>
                        <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#fee2e2',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 11, fontWeight: 700, color: '#dc2626' }}>!</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>
                            {dev.hostname || dev.device?.hostname || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            Risk score: {dev.riskScore || dev.risk_score || '—'}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2',
                          color: '#dc2626', padding: '2px 7px', borderRadius: 10 }}>HIGH RISK</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ════ ROW 4: Recent Activity ════ */}
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.8px',
              textTransform: 'uppercase', marginBottom: 12 }}>
              ▸ Recent Activity
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

              {/* Latest Patch Releases */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 12 }}>
                  Latest Patch Releases
                </div>
                {d.releases.length === 0 ? (
                  <div className="empty">No releases yet — click Check Now in Patch Releases</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {d.releases.map((r, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '7px 10px', background: '#f8fafc', borderRadius: 8 }}>
                        <span style={{ fontSize: 14 }}>
                          {r.category === 'os' ? '🪟' : r.category === 'browser' ? '🌐' : '📦'}
                        </span>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: '#1e2a3b' }}>{r.patchName}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {r.vendor} · v{r.newVersion} · {timeAgo(r.createdAt)}
                          </div>
                        </div>
                        <span style={{ fontSize: 10, fontWeight: 700,
                          background: SEV_BG[r.severity] || '#f1f5f9',
                          color: SEV_COLOR[r.severity] || '#64748b',
                          padding: '2px 7px', borderRadius: 10 }}>
                          {r.severity}
                        </span>
                        {r.devicesQueued > 0 && (
                          <span style={{ fontSize: 10, color: '#d97706', fontWeight: 600 }}>
                            ⏳{r.devicesQueued}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Devices with Pending Patches */}
              <div className="panel" style={{ padding: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#1e2a3b', marginBottom: 12 }}>
                  Devices Requiring Attention
                </div>
                {d.pendingDevices.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: 24, color: '#16a34a' }}>
                    <div style={{ fontSize: 32 }}>✅</div>
                    <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8 }}>All devices up to date</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {d.pendingDevices.map((dev, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 12px', background: '#f8fafc', borderRadius: 8,
                        borderLeft: dev.criticalCount > 0 ? '3px solid #dc2626' : '3px solid #d97706' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>
                            {dev.device?.hostname || '—'}
                          </div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>
                            {dev.device?.os || '—'} · {dev.pendingPatches?.length || 0} patches pending
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4 }}>
                          {dev.criticalCount > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, background: '#fee2e2',
                              color: '#dc2626', padding: '2px 7px', borderRadius: 10 }}>
                              {dev.criticalCount} critical
                            </span>
                          )}
                          {dev.highCount > 0 && (
                            <span style={{ fontSize: 10, fontWeight: 700, background: '#ffedd5',
                              color: '#ea580c', padding: '2px 7px', borderRadius: 10 }}>
                              {dev.highCount} high
                            </span>
                          )}
                          <span style={{ fontSize: 10, fontWeight: 600,
                            background: dev.device?.status === 'online' ? '#dcfce7' : '#f1f5f9',
                            color: dev.device?.status === 'online' ? '#16a34a' : '#94a3b8',
                            padding: '2px 7px', borderRadius: 10 }}>
                            {dev.device?.status === 'online' ? '● Online' : '○ Offline'}
                          </span>
                        </div>
                      </div>
                    ))}
                    {d.pendingSummary?.devicesWithPending > 5 && (
                      <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', paddingTop: 4 }}>
                        +{d.pendingSummary.devicesWithPending - 5} more devices with pending patches
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      ))}

      {/* ════════════════════════════════════════════════════════════════════
          SERVER DIAGNOSTICS TAB
      ════════════════════════════════════════════════════════════════════ */}
      {tab === 'diagnostics' && (
        <>
          {diagError && <div className="alert error" style={{ marginBottom: 16 }}>{diagError}</div>}
          {diagLoading && <div className="loading">Running diagnostics...</div>}

          {/* Server Endpoints */}
          <div className="panel" style={{ marginBottom: 20 }}>
            <div className="panel-header">
              <div className="panel-title"><div className="panel-title-icon">🌐</div>Server Endpoints</div>
              <span style={{ fontSize: 11, color: '#94a3b8' }}>IPs agents use to connect · LAN + Public + Failover</span>
            </div>
            <table>
              <thead>
                <tr><th>Role</th><th>IP Address</th><th>Port</th><th>Protocol</th><th>Status</th></tr>
              </thead>
              <tbody>
                {(endpoints?.endpoints || []).map((ep, i) => (
                  <tr key={i}>
                    <td>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 10px', borderRadius: 20,
                        background: ep.type === 'failover' ? '#fef3c7' : ep.type === 'public' ? '#dbeafe' : '#dcfce7',
                        color:      ep.type === 'failover' ? '#d97706' : ep.type === 'public' ? '#1d4ed8' : '#16a34a',
                      }}>
                        {ep.role}
                      </span>
                    </td>
                    <td><code style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b',
                      background: '#f8fafc', padding: '2px 8px', borderRadius: 4 }}>{ep.ip}</code></td>
                    <td style={{ fontSize: 13 }}>{ep.port}</td>
                    <td style={{ fontSize: 12, color: '#94a3b8' }}>{ep.protocol}</td>
                    <td><span className="badge success">● Listening</span></td>
                  </tr>
                ))}
                {(!endpoints?.endpoints?.length) && (
                  <tr><td colSpan={5} className="empty">Loading endpoints...</td></tr>
                )}
              </tbody>
            </table>
            <div style={{ padding: '16px 20px', borderTop: '1px solid #f0f4ff',
              background: endpoints?.publicConfigured ? '#f0fdf4' : '#fffbeb' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#1e2a3b' }}>
                  {endpoints?.publicConfigured ? '✔ Public IP configured' : '⚠ No public IP configured'}
                  {endpoints?.failoverConfigured && <span style={{ color: '#d97706', marginLeft: 8 }}>· ⚡ Failover IP configured</span>}
                </span>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>— {endpoints?.hint || ''}</span>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>Public IP / Hostname:</span>
                <input className="form-input" style={{ width: 240, padding: '6px 10px', fontSize: 13 }}
                  placeholder="e.g. patchsphere.covasant.com" value={publicIp}
                  onChange={e => setPublicIp(e.target.value)} />
                <button className="btn btn-primary btn-sm" onClick={savePublicIp} disabled={saving}>
                  {saving ? '⟳ Saving...' : 'Save & Apply'}
                </button>
                {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Error') ? '#dc2626' : '#16a34a' }}>{saveMsg}</span>}
              </div>
            </div>
          </div>

          {!diagData && !diagLoading && !diagError && (
            <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
              Click <strong>↻ Run Checks</strong> to load server diagnostics
            </div>
          )}

          {diagData && (
            <>
              <div className="table-wrap section-gap" style={{ padding: 20, marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>Overall Status</div>
                  </div>
                  <StatusBadge value={diagData.overall?.status || 'unknown'} />
                </div>
                {diagData.overall?.issues?.length === 0 && (
                  <div style={{ marginTop: 12, color: '#16a34a', fontSize: 13 }}>✔ All checks passed</div>
                )}
                {diagData.overall?.issues?.map((issue, i) => (
                  <div key={i} className="alert error" style={{ marginTop: 8 }}>✘ {issue}</div>
                ))}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <DiagSection title="Server"      icon="🖥️" data={diagData.server} />
                  <DiagSection title="Environment" icon="⚙️" data={diagData.environment} />
                  <DiagSection title="Services"    icon="🔧" data={diagData.services} />
                </div>
                <div>
                  <DiagSection title="Database" icon="🗄️" data={{
                    connection: diagData.database?.connection,
                    ...Object.fromEntries(
                      Object.entries(diagData.database?.tables || {}).map(([k,v]) => [`table:${k}`, `${v} rows`])
                    )
                  }} />
                  <DiagSection title="Network"    icon="🌐" data={diagData.network} />
                  <DiagSection title="Filesystem" icon="📁" data={diagData.filesystem} />
                </div>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
